import React, { useState, useEffect } from 'react';
import { AppBar, Toolbar, IconButton, Typography, Box, Skeleton } from '@mui/material';
import SettingsRoundedIcon from '@mui/icons-material/SettingsRounded';
import PaymentRoundedIcon from '@mui/icons-material/PaymentRounded';
import ArrowBackIosRoundedIcon from '@mui/icons-material/ArrowBackIosRounded';
import EditProfile from '../components/profile/EditProfile';
import Statistics from '../components/profile/Statistics';
import Badges from '../components/profile/Badges';
import Invite from '../components/profile/Invite';
import CreditOverview from '../components/profile/CreditOverview';
import ManageInvitations from '../components/profile/ManageInvitations';
import EditNotification from '../components/profile/EditNotification';
import ManageSubscription from '../components/profile/ManageSubscription';
import MeditationTimeSelector from '../components/profile/MeditationTimeSelector';
import { useAuth } from '../contexts/AuthContext';
import useDeviceInfo from '../utils/useDeviceInfo';
import { useTranslation } from 'react-i18next'; // Importer le hook i18n

const ProfilePage = ({ onClose, isOpen }) => {
  const { t } = useTranslation(); // Hook pour les traductions
  const [isProfileDrawerOpen, setIsProfileDrawerOpen] = useState(false);
  const [isNotificationDrawerOpen, setIsNotificationDrawerOpen] = useState(false);
  const [isSubscriptionDrawerOpen, setIsSubscriptionDrawerOpen] = useState(false);
  const { currentUser, refreshUser } = useAuth();
  const [loading, setLoading] = useState(true);
  const { paddingTop, paddingBottom } = useDeviceInfo();

  // Rafraîchir les données utilisateur à chaque ouverture
  useEffect(() => {
    const fetchUser = async () => {
      if (isOpen) {
        try {
          setLoading(true);
          await refreshUser(); // Rafraîchir les données utilisateur
        } catch (error) {
          console.error('Erreur lors du rafraîchissement des données utilisateur :', error);
        } finally {
          setLoading(false);
        }
      }
    };

    fetchUser();
  }, [isOpen]); // Déclenche le rafraîchissement uniquement lorsque `isOpen` passe à true

  const handleProfileDrawerOpen = () => {
    setIsProfileDrawerOpen(true);
  };

  const handleProfileDrawerClose = () => {
    setIsProfileDrawerOpen(false);
    refreshUser();
  };

  const handleNotificationDrawerClose = () => {
    setIsNotificationDrawerOpen(false);
  };

  const handleSubscriptionDrawerOpen = () => {
    setIsSubscriptionDrawerOpen(true);
  };

  const handleSubscriptionDrawerClose = () => {
    setIsSubscriptionDrawerOpen(false);
    refreshUser();
  };

  return (
    <Box sx={{ pt: paddingTop + 2 }}>
      <AppBar position="static" sx={{ backgroundColor: 'transparent', boxShadow: 'none', verticalAlign: 'middle', px: 1 }}>
        <Toolbar>
          <IconButton
            size="small"
            sx={{
              p: 0.5,
              border: '1px solid #ddd',
              borderRadius: '50%',
              mr: 1,
            }}
            aria-label={t('back')}
            onClick={onClose}
          >
            <ArrowBackIosRoundedIcon fontSize="small" />
          </IconButton>
          <Box sx={{ display: 'flex', alignItems: 'center', flexGrow: 1 }}>
            {loading ? (
              <Skeleton variant="text" width={100} animation="wave" />
            ) : (
              <Typography variant="h5" sx={{ color: 'black', lineHeight: '1em' }}>
                {currentUser?.firstName || t('loading')}
              </Typography>
            )}
            <Typography
              variant="body"
              sx={{
                backgroundColor: 'secondary.main',
                px: 1,
                py: 0.5,
                borderRadius: 2,
                ml: 1,
                color: 'black',
                lineHeight: '1em',
              }}
            >
              {currentUser?.experiencePoints} {t('xp')}
            </Typography>
          </Box>

          <IconButton color="inherit" onClick={handleSubscriptionDrawerOpen} sx={{ color: 'black' }}>
            <PaymentRoundedIcon />
          </IconButton>
          <IconButton color="inherit" onClick={handleProfileDrawerOpen} sx={{ color: 'black' }}>
            <SettingsRoundedIcon />
          </IconButton>
        </Toolbar>
      </AppBar>

      <EditProfile isOpen={isProfileDrawerOpen} onClose={handleProfileDrawerClose} onUpdate={refreshUser} />
      <EditNotification isOpen={isNotificationDrawerOpen} onClose={handleNotificationDrawerClose} />
      <ManageSubscription isOpen={isSubscriptionDrawerOpen} onClose={handleSubscriptionDrawerClose} />

      <Box sx={{ pt: 1, px: 3, pb: 10 }}>
        <CreditOverview />
        <ManageInvitations />
        <Statistics />
        <MeditationTimeSelector />
        <Invite />
        <Badges />
      </Box>
    </Box>
  );
};

export default ProfilePage;
